import {
	fetchFileRequest,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import {
	CapTableRail,
	CreateShareRegisterPdfTaskRes,
	GenerateShareRegisterRes,
	GetHoldingsListForPersonDto,
	GetRegularViewHoldingsCsvDto,
	GetWideViewHoldingsCsvDto,
	HoldingDetailRes,
	HoldingEventDto,
	HoldingRes,
	ShareRegisterPdfTaskStatusRes,
} from '@dealstack/shared/lib/generated';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';

export const getHoldingsListForPerson = async (
	endpoint: string,
	args: GetHoldingsListForPersonDto
) => {
	return makeRequest<ApiV1.Response<HoldingRes[]>>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((response) => response.payload);
};

export const getHoldingDetails = async (body: {
	securityId: string;
	personId: string;
	mipId?: string;
	// CAP-1988: which title rail to read on nominee structures. The backend maps it onto
	// `get_entity_holdings`' p_title selector: BENEFICIAL reads the beneficial-rail columns on
	// title-split deals, LEGAL (or omitted) reads the legal-rail columns.
	rail?: CapTableRail;
}) => {
	return makeRequest<ApiV1.Response<HoldingDetailRes | null>>({
		endpoint: formatUnicorn(API_PATHS.CAP_HOLDINGS_PERSON_PERSONID_SECURITY_SECURITYID_GET, {
			securityId: body.securityId,
			personId: body.personId,
		}),
		queryString: {
			...(body.mipId && { mipId: body.mipId }),
			...(body.rail && { rail: body.rail }),
		},
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload || null);
};

export const getHoldingEvents = async ({
	securityId,
	personId,
	filterDate,
	mipId,
	rail,
}: {
	securityId: string;
	personId: string;
	filterDate?: string;
	mipId?: string;
	// CAP-1988: on a nominee structure an event belongs to the rail whose interest it moved, so the
	// log drops the events that moved the other one. Omitted, every event is listed.
	rail?: CapTableRail;
}) => {
	return makeRequest<ApiV1.Response<HoldingEventDto[]>>({
		endpoint: formatUnicorn(
			API_PATHS.CAP_HOLDINGS_PERSON_PERSONID_SECURITY_SECURITYID_EVENTS_GET,
			{
				securityId,
				personId,
			}
		),
		queryString: {
			...(filterDate && { filterDate: browserDateToMidnightUTC(filterDate) }),
			...(mipId && { mipId }),
			...(rail && { rail }),
		},
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getWideViewHoldingsCSV = async (args: GetWideViewHoldingsCsvDto) => {
	return makeRequest<ApiV1.Response<string>>({
		endpoint: API_PATHS.CAP_WIDE_VIEW_CSV_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const getRegularViewHoldingsCSV = async (args: GetRegularViewHoldingsCsvDto) => {
	return makeRequest<ApiV1.Response<string>>({
		endpoint: API_PATHS.CAP_REGULAR_VIEW_CSV_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	}).then((r) => r.payload);
};

export const getDownloadExcel = async (args: {
	personId: string;
	isOwnership: boolean;
	filterDate?: string;
	// CAP-1973: the title rail the workbook is taken on, so it matches the table it was exported
	// from. Omitted (or on a deal without a nominee structure) reads the legal register.
	rail?: CapTableRail;
}) => {
	const queryParams = new URLSearchParams();
	queryParams.append('personId', args.personId);
	queryParams.append('isOwnership', args.isOwnership.toString());

	if (args.filterDate) {
		queryParams.append('filterDate', args.filterDate);
	}

	if (args.rail) {
		queryParams.append('rail', args.rail);
	}

	const endpoint = `${API_PATHS.CAP_HOLDINGS_EXCEL_GET}?${queryParams.toString()}`;

	const res = await fetchFileRequest(endpoint, {});
	if (!res) {
		throw new Error('Failed to download file');
	}

	return res.blob();
};

export const getShareRegisterPdf = async (args: {
	issuerId: string;
	filterDate?: string;
}): Promise<GenerateShareRegisterRes> => {
	const queryParams = new URLSearchParams();

	if (args.filterDate) {
		queryParams.append('date', args.filterDate);
	}

	const queryString = queryParams.toString();
	const endpoint = formatUnicorn(API_PATHS.CAP_SHARE_REGISTER_PDF_ISSUERID_GET, {
		issuerId: args.issuerId,
	});

	const response = await makeRequest<ApiV1.Response<GenerateShareRegisterRes>>({
		endpoint: `${endpoint}${queryString ? `?${queryString}` : ''}`,
		method: HTTP_METHOD.GET,
	});

	if (!response.payload) {
		throw new Error('Failed to generate share register PDF');
	}

	return response.payload;
};

export const startShareRegisterPdfGeneration = async (args: {
	issuerId: string;
	date?: string;
}): Promise<CreateShareRegisterPdfTaskRes> => {
	const endpoint = formatUnicorn(API_PATHS.CAP_SHARE_REGISTER_PDF_ISSUERID_GENERATE_POST, {
		issuerId: args.issuerId,
	});
	const response = await makeRequest<ApiV1.Response<CreateShareRegisterPdfTaskRes>>({
		endpoint,
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ date: args.date }),
	});

	if (!response.payload) {
		throw new Error('Failed to start share register PDF generation');
	}

	return response.payload;
};

export const getShareRegisterPdfTaskStatus = async (
	endpoint: string
): Promise<ShareRegisterPdfTaskStatusRes> => {
	const response = await makeRequest<ApiV1.Response<ShareRegisterPdfTaskStatusRes>>({
		endpoint,
		method: HTTP_METHOD.GET,
	});

	if (!response.payload) {
		throw new Error('Failed to fetch share register task status');
	}

	return response.payload;
};
